import { useLayoutEffect, useRef, type CSSProperties } from 'react';
import { PianoView, type PianoOptions } from './piano';
import { DEFAULT_PIANO_RANGE } from './piano-geometry';
import type { VizNote } from './types';

export interface PianoCanvasProps {
  /** Range in use [lowMidi, highMidi]; shown as whole octaves (≥ 2). Default C3–B5. */
  range?: readonly [number, number];
  active: readonly VizNote[];
  context: readonly VizNote[];
  /** Touch input defaults to on when `onPick` is given. */
  options?: Partial<PianoOptions>;
  onPick?: (midi: number) => void;
  /** CSS px (default 110). */
  height?: number;
  className?: string;
  style?: CSSProperties;
}

/** React wrapper around PianoView; pushes prop changes into the canvas view (redraws only on change). */
export function PianoCanvas({ range, active, context, options, onPick, height = 110, className, style }: PianoCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewRef = useRef<PianoView | null>(null);
  const pickRef = useRef(onPick);

  useLayoutEffect(() => {
    pickRef.current = onPick;
  });

  useLayoutEffect(() => {
    const view = new PianoView(canvasRef.current!);
    viewRef.current = view;
    view.onPick((m) => pickRef.current?.(m));
    return () => {
      view.destroy();
      viewRef.current = null;
    };
  }, []);

  const lo = range?.[0] ?? DEFAULT_PIANO_RANGE[0];
  const hi = range?.[1] ?? DEFAULT_PIANO_RANGE[1];
  useLayoutEffect(() => {
    viewRef.current?.setRange(lo, hi);
  }, [lo, hi]);

  const touch = options?.touchInput ?? !!onPick;
  useLayoutEffect(() => {
    viewRef.current?.setOptions({ ...options, touchInput: touch });
  }, [options, touch]);

  useLayoutEffect(() => {
    viewRef.current?.setNotes(active, context);
  }, [active, context]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      role="img"
      aria-label="Piano keyboard"
      style={{ display: 'block', width: '100%', height, touchAction: 'manipulation', ...style }}
    />
  );
}
